import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";

import SGrid from "../../components/grid/grid.component";
import {fetch} from "../../shared/api/v1";
import {useAuth} from "../../providers/auth/auth-provider";
import {useCabinet} from "../../models/cabinet/cabinet-provider";
import {useMessage} from "../../providers/message/message-provider";
import type {components} from "../../shared/api/v1/openapi.types";
import {getConnectionStatusLabel} from "../../constants/connection";

type EventEntityT = components["schemas"]["Event"];

export const EventsList = () => {
  const {token} = useAuth();
  const {connectionMap} = useCabinet();
  const {showError} = useMessage();
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventEntityT[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  useEffect(() => {
    let isMounted = true;
    const loadEvents = async () => {
      if (!token) {
        return;
      }
      setIsLoading(true);
      const response = await fetch.GET("/events/my", {
        params: {
          query: {
            page,
            limit: pageSize,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!isMounted) {
        return;
      }
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        showError({
          message: message ?? "Ошибка загрузки событий",
        });
        setEvents([]);
        setIsLoading(false);
        return;
      }
      const payload = response.data as { events?: EventEntityT[]; total?: number } | EventEntityT[] | undefined;
      const data: EventEntityT[] = Array.isArray(payload)
        ? payload
        : payload && Array.isArray(payload.events)
          ? payload.events
          : [];
      setEvents(data);
      setTotal(typeof (payload as { total?: number })?.total === "number" ? (payload as { total?: number }).total! : data.length);
      setIsLoading(false);
    };
    loadEvents();
    return () => {
      isMounted = false;
    };
  }, [page, pageSize, showError, token]);

  const formatEventMessage = (event: EventEntityT) => {
    const partnerName = event.partner?.name ?? event.partner?.shortName;
    if (event.general === 0) {
      return `Сформирована новая заявка${partnerName ? ` (${partnerName})` : ""}`;
    }
    if (event.general === 1) {
      const statusValue = Number(event.message);
      const statusLabel = Number.isFinite(statusValue)
        ? getConnectionStatusLabel(statusValue)
        : event.message;
      return `Изменение статуса${statusLabel ? `: ${statusLabel}` : ""}${partnerName ? ` (${partnerName})` : ""}`;
    }
    if (event.general === 4) {
      return `Получены документы от ТСО${partnerName ? ` (${partnerName})` : ""}`;
    }
    if (event.general === 6) {
      return `Получены документы от ГП${partnerName ? ` (${partnerName})` : ""}`;
    }
    const base = event.message ?? "Событие";
    return `${base}${partnerName ? ` (${partnerName})` : ""}`;
  };

  const getEventTab = (event: EventEntityT) => {
    switch (event.general) {
      case 1:
      case 2:
        return "manage";
      case 3:
        return "preview";
      case 4:
        return "result-files";
      case 6:
        return "sbit-files";
      default:
        return "edit";
    }
  };

  const buildEventUrl = (event: EventEntityT) => {
    if (event.document !== "connection" || !event.documentId) {
      return undefined;
    }
    const tab = getEventTab(event);
    return `/cabinet/connection/${event.documentId}?tab=${tab}`;
  };

  const columns = useMemo(
    () => [
      {
        headerStyle: {width: "1rem"},
      },
      {
        field: "message",
        header: "Сообщение",
        body: (item: EventEntityT) => (
          <span>{formatEventMessage(item)}</span>
        ),
      },
      {
        field: "documentId",
        header: "Адрес",
        body: (item: EventEntityT) => {
          if (item.document !== "connection") {
            return <span>-</span>;
          }
          const connectionId = Number(item.documentId);
          const address = Number.isFinite(connectionId)
            ? connectionMap[connectionId]?.address
            : undefined;
          return <span>{address ?? "-"}</span>;
        },
      },
      {
        headerStyle: {width: "170px"},
        field: "createdAt",
        header: "Получено",
        body: (item: EventEntityT) => (
          <span>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleString("ru-RU", {
                dateStyle: "short",
                timeStyle: "short",
              })
              : "-"}
          </span>
        ),
      },
    ],
    [connectionMap]
  );

  return (
    <SGrid
      header={<span>События</span>}
      selectionMode={"single"}
      value={events}
      loading={isLoading}
      rowHover
      rowClassName={() => 'cursor-pointer'}
      paginator
      rows={pageSize}
      totalRecords={total}
      first={(page - 1) * pageSize}
      onPage={(e) => {
        const nextRows = e.rows ?? pageSize;
        const nextPage = Math.floor((e.first ?? 0) / nextRows) + 1;
        setPageSize(nextRows);
        setPage(nextPage);
      }}
      onRowClick={({data}) => {
        const event = data as EventEntityT;
        const url = buildEventUrl(event);
        if (url) {
          navigate(url);
        }
      }}
      columns={columns}
    />
  );
};